import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtDateTime, parseApiError } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataStateRow } from "@/components/ui/data-state";
import { Braces, RefreshCcw, Search } from "lucide-react";

const DEFAULT_FILTERS = { entity: "", entity_id: "", limit: "50" };

function payloadText(payload) {
  if (payload == null || payload === "") return "{}";
  if (typeof payload === "string") {
    try {
      return JSON.stringify(JSON.parse(payload), null, 2);
    } catch {
      return payload;
    }
  }
  return JSON.stringify(payload, null, 2);
}

function actionLabel(row) {
  return row.action || "unknown";
}

export default function AuditHistory() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (applied.entity.trim()) params.set("entity", applied.entity.trim());
    if (applied.entity_id.trim()) params.set("entity_id", applied.entity_id.trim());
    if (applied.limit) params.set("limit", applied.limit);
    return params.toString();
  }, [applied]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get(`/audit-logs${query ? `?${query}` : ""}`);
      setRows(r.data);
      setActive(r.data[0] || null);
    } catch (e) {
      setError(parseApiError(e, "Không tải được audit history").message);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  const submit = (e) => {
    e.preventDefault();
    setApplied(filters);
  };

  return (
    <div data-testid="page-audit-history">
      <PageHeader
        kicker="Admin • Audit"
        title="Audit History"
        breadcrumb={["Công cụ", "Audit History"]}
        actions={
          <Button type="button" variant="outline" onClick={load} className="rounded-sm" data-testid="btn-refresh-audit">
            <RefreshCcw className="w-4 h-4 mr-1.5" />Tải lại
          </Button>
        }
      />

      <div className="p-6 lg:p-8 space-y-4">
        <form onSubmit={submit} className="bg-white border border-stone-200 rounded-sm p-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_120px_auto] gap-2">
          <Input
            value={filters.entity}
            onChange={(e) => setFilters({ ...filters, entity: e.target.value })}
            placeholder="entity: task, employee..."
            className="h-9 rounded-sm"
            data-testid="audit-filter-entity"
          />
          <Input
            value={filters.entity_id}
            onChange={(e) => setFilters({ ...filters, entity_id: e.target.value })}
            placeholder="entity_id"
            className="h-9 rounded-sm"
            data-testid="audit-filter-entity-id"
          />
          <Input
            value={filters.limit}
            onChange={(e) => setFilters({ ...filters, limit: e.target.value })}
            type="number"
            min="1"
            max="200"
            className="h-9 rounded-sm"
            data-testid="audit-filter-limit"
          />
          <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-filter-audit">
            <Search className="w-4 h-4 mr-1.5" />Lọc
          </Button>
        </form>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-4">
          <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold">Recent rows</div>
                <div className="font-serif text-lg font-bold">{rows.length} audit logs</div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold">Time</th>
                    <th className="text-left px-3 py-2 font-bold">Action</th>
                    <th className="text-left px-3 py-2 font-bold">Entity</th>
                    <th className="text-left px-3 py-2 font-bold">Entity ID</th>
                    <th className="text-left px-3 py-2 font-bold">User</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {loading && <DataStateRow colSpan={5} type="loading" message="Đang tải audit logs..." />}
                  {!loading && error && <DataStateRow colSpan={5} type="error" message={error} onRetry={load} />}
                  {!loading && !error && rows.length === 0 && <DataStateRow colSpan={5} type="empty" message="Không có audit log phù hợp" />}
                  {!loading && !error && rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setActive(row)}
                      className={`cursor-pointer hover:bg-stone-50 ${active?.id === row.id ? "bg-emerald-50" : ""}`}
                      data-testid={`audit-row-${row.id}`}
                    >
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-stone-600">{fmtDateTime(row.created_at)}</td>
                      <td className="px-3 py-2">
                        <span className="px-1.5 py-0.5 bg-stone-100 text-stone-800 rounded-sm font-semibold text-xs">{actionLabel(row)}</span>
                      </td>
                      <td className="px-3 py-2 font-semibold">{row.entity || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs text-stone-600 max-w-[220px] truncate">{row.entity_id || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs text-stone-600 max-w-[180px] truncate">{row.user_id || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <aside className="bg-white border border-stone-200 rounded-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2">
              <Braces className="w-4 h-4 text-emerald-800" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold">Payload</div>
                <div className="font-serif text-lg font-bold">{active ? actionLabel(active) : "Chưa chọn"}</div>
              </div>
            </div>
            {active ? (
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <Meta label="Entity" value={active.entity} />
                  <Meta label="Entity ID" value={active.entity_id} />
                  <Meta label="User ID" value={active.user_id} />
                  <Meta label="Created" value={fmtDateTime(active.created_at)} />
                </div>
                <pre className="bg-stone-950 text-stone-50 rounded-sm p-3 text-xs overflow-auto max-h-[520px]" data-testid="audit-payload">
                  {payloadText(active.payload)}
                </pre>
              </div>
            ) : (
              <div className="px-4 py-10 text-sm text-stone-500 text-center">Chọn một audit row để xem payload JSON</div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="border-b border-stone-100 pb-1.5 min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">{label}</div>
      <div className="font-mono text-xs text-stone-800 truncate">{value || "—"}</div>
    </div>
  );
}
